import {Box, Grid, Pagination, PaginationItem, Table, TableBody, TableHead, TableRow, Tooltip, Typography} from "@mui/material";
import TableCell from "@mui/material/TableCell";
import {getFormattedProperty} from "../../utils/utils";
import {getTapeIcon} from "./tapesIcons";
import {TYPICAL_COLUMN_UI_CLASS} from "./tableCustomization";
import {highlightText as sharedHighlightText, useStyles} from "../../utils/uiUtils";
import {tapeStatusLabel} from "../../utils/tapeStatus";

/**
 * Helper function that generates the internal content used later to populate the tooltip of a table cell.
 * @param {Object | null | undefined} entity - The entity (Object) containing the raw value to be displayed in the tooltip (or null).
 * @param tooltTipList - The list of tooltip lines to be included.If entity is not provided, each tooltTipList entry needs to contain a value.
 * @returns {React.JSX.Element}
 */
export function dynamicTooltipConstruction(entity, tooltTipList) {
    const maxCharactersPerLine = 80; // Limit each line to 80 characters
    const maxTotalRows = 40; // Limit the total rows in the tooltip
    let keyCounter = 0;

    /**
     * Function to split long values into lines of 80 characters
     * @param value
     * @returns {*[]}
     */
    const wrapValue = (value) => {
        const lines = [];
        for (let i = 0; i < value.length; i += maxCharactersPerLine) {
            lines.push(value.slice(i, i + maxCharactersPerLine));
        }
        return lines;
    };

    // Build tooltip content and enforce the 40-row limit
    const rows = [];
    for (const tooltipEntry of tooltTipList) {
        const tooltipKey = tooltipEntry?.name;
        let tooltipEntryValue;
        if (entity) {
            tooltipEntryValue = getFormattedProperty(entity, tooltipEntry?.mnemonic, tooltipEntry?.ui_class);
        } else {
            tooltipEntryValue = getFormattedProperty(tooltipEntry?.value, null, tooltipEntry?.ui_class);
        }
        const tooltipEntryWrappedValue = wrapValue(tooltipEntryValue);

        tooltipEntryWrappedValue.forEach((line, index) => {
            keyCounter++;
            rows.push(
                <div key={`dynamicTooltip-line-${keyCounter}`}>
                    {index === 0 && tooltipKey ? (<strong>{tooltipKey} : </strong>) : ""}{line}
                </div>)
        });
        if (rows.length >= maxTotalRows) break;
    }

    // Return empty string (falsy) when no content — MUI Tooltip with falsy title is suppressed entirely
    if (rows.length === 0) return "";
    return <>{rows.slice(0, maxTotalRows)}</>;
}

/**
 * React component to render the pagination of the table.
 * @param {boolean} showPagination - A boolean to indicate if the pagination should be displayed.
 * @param {number} entriesCount - The number of entries in the table.
 * @param {number} currentPage - The current page of the table.
 * @param {number} totalPages - The total number of pages in the table.
 * @param {function} handlePageChangeCallback - A callback function to be called when the page is changed.
 * @returns {Element}
 * @constructor
 */
const TablePagination = ({
                             showPagination,
                             entriesCount,
                             currentPage,
                             totalPages,
                             handlePageChangeCallback
                         }) => {
    const startIndex = (currentPage - 1) * 10 + 1;
    const endIndex = Math.min(currentPage * 10, entriesCount);

    //Pagination
    return (showPagination ?
            <Grid item xs={12}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    padding={2}
                    style={{
                        width: "100%",
                        border: "1px solid #C7CCD0",
                        minWidth: "55%",
                    }}>
                    <Grid item ml={2}>
                        <Typography
                            sx={{
                                fontFamily: "Poppins",
                                fontSize: "16px",
                                lineHeight: "24px",
                                fontWeight: 400,
                                color: "#FFFFFF",
                            }}>
                            {`Showing ${entriesCount ? startIndex : 0}-${endIndex ?? 0} of ${entriesCount ?? 0} Items`}
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Pagination
                            component="div"
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChangeCallback}
                            sx={{
                                "& .MuiPaginationItem-root": {
                                    minWidth: "30px",
                                    height: "30px",
                                    borderRadius: "16px",
                                    border: "1px solid #FFFFFF",
                                    background: "#3A3E3F",
                                    color: "#FFFFFF",
                                    margin: "0 4px",
                                    padding: "0 8px",
                                    "&.Mui-selected": {
                                        background: "#FFC540",
                                        color: "#3A3E3F",
                                    },
                                },
                                "& .MuiPaginationItem-page.Mui-selected:hover": {
                                    background: "#FFC540",
                                    color: "#3A3E3F",
                                },
                                "& .MuiPaginationItem-page:hover": {
                                    background: "#FFC540",
                                    color: "#3A3E3F",
                                },
                                "& .MuiPaginationItem-previous, & .MuiPaginationItem-next": {
                                    width: "100px",
                                    height: "40px",
                                    borderRadius: "8px",
                                    border: "1px solid #FFFFFF",
                                    color: "#FFFFFF",
                                    background: "#3A3E3F",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                },
                                "& .MuiPaginationItem-previous:hover, & .MuiPaginationItem-next:hover": {
                                    background: "#FFC540",
                                    color: "#3A3E3F",
                                },
                            }}
                            renderItem={(item) => <PaginationItem {...item} />}
                        />
                    </Grid>
                </Grid>
            </Grid>
            : <></>
    );
};

/**
 * @typedef {Object} TooltipEntry - Defines a single entry in the tooltip.
 * @property {string} name - Attribute display name. It will be bolded, followed by a colon and the value.
 * @property {string} mnemonic - Attribute mnemonic to get the value from the entity attributes.
 * @property {string} ui_class - Attribute UI class to format the value. Accepted values can be found in utils.js -> formatData function.
 */

/**
 * @typedef {Object} ColumnConfig - Different configuration options for a column in the table.
 * @property {string} name - Column display name.
 * @property {string} mnemonic - Name of the property to be displayed in the column. It will be used to get the value from the entity attributes.
 * @property {string} ui_class - Column UI class to format the value. Accepted values can be found in utils.js -> formatData function.
 * @property {boolean} searchable - A boolean to indicate if the column is searchable. If true, the search query will be highlighted in the column value.
 * @property {string} special_render - Special render methods implemented for this column type. Accepted values: tape_photo.
 * @property {TooltipEntry[]} tooltip - A list of Tooltip objects that define how different properties compose to form the tooltip for an entry. Data comes from the entity attributes. Multiple attributes can be combined in a single tooltip.
 * @property {function} click_callback - A callback function to be called when the entry is clicked. It will be passed the entire row object.
 * @property {function} custom_render - A function that generates a custom component to be displayed in the column. It will be passed the entire row object and the formatted property value.
 * @property {string} header_align - Alignment of the column header text. Accepted values: left, center, right.
 * @property {string} content_align - Alignment of cell content. When set, content is tightly packed to the specified side. Accepted values: left, center, right. When absent, content spreads naturally.
 */

/**
 * Function to render a dynamic table component.
 * @param {Object[]} data - A list of objects containing the data to be displayed in the table.
 * @param {ColumnConfig[]} columnsConfig - A list of objects containing the columns to be displayed in the table. Minimum details: name, mnemonic. Optional details: ui_class, special_render, searchable, tooltip.
 * @param {boolean} dataContainsFormatting - A boolean to indicate if the data contains formatting type, or it should be found in columnsConfig.
 * @param {string | undefined} searchQuery - A string to be searched in the table data.
 * @param {boolean} showPagination - A boolean to indicate if the pagination should be displayed.
 * @param {number} entriesCount - The number of entries in the table.
 * @param {number} currentPage - The current page of the table.
 * @param {number} totalPages - The total number of pages in the table.
 * @param {function} handlePageChangeCallback - A callback function to be called when the page is changed.
 * @returns {Element}
 */
const DataTable = ({
                       data,
                       columnsConfig,
                       dataContainsFormatting,
                       searchQuery = undefined,
                       showPagination,
                       entriesCount,
                       currentPage,
                       totalPages,
                       handlePageChangeCallback
                   }) => {

    const classes = useStyles();

    /**
     * Function to render a table cell for no data situation.
     * @returns {Element}
     */
    const noDataCell = () => {
            return <TableCell colSpan={2} align="center">
                <div style={{textAlign: "center", width: "50%", margin: "0", left: "50%", alignItems: "center", color: "#fff"}}>Data not Found</div>
            </TableCell>;
        }
    ;

    /**
     * Function to highlight the search query in the text. It generates components and the ones that match the search query, have the highlight class.
     * @param id - id not used
     * @param {string} text - Text to be compared with the search query
     * @returns - Array of components
     */
    const highlightText = (id, text) => sharedHighlightText(text, searchQuery);

    /**
     * helper function that generates the internal content used later to populate the content of a table cell
     * @param column
     * @param row
     * @returns {React.JSX.Element|string|*[]|*}
     */
    const dynamicContentForCellHelper = (column, row) => {
        let columnType;
        if (!dataContainsFormatting) {
            // get the ui_class from the column configuration
            columnType = column?.ui_class;
        } else {
            if (column?.ui_class) {
                // if the column configuration has a ui_class, use it to format the value
                columnType = column?.ui_class;
            } else {
                // get the ui_class from the row
                if (row?.attributes && row.attributes?.[column.mnemonic]) {
                    if (row?.attributes?.[column.mnemonic]?.ui_class) {
                        // get ui_class from attributes -> mnemonic
                        columnType = row.attributes[column.mnemonic].ui_class;
                    } else {
                        // default to data_type from attributes -> mnemonic
                        columnType = row.attributes[column.mnemonic]["data_type"];
                    }
                } else {
                    // try getting the ui_class from TYPICAL_COLUMN_UI_CLASS if it exists or default to 'str'
                    columnType = TYPICAL_COLUMN_UI_CLASS[column.mnemonic] ? TYPICAL_COLUMN_UI_CLASS[column.mnemonic] : 'str';
                }
            }
        }

        const formattedProperty = getFormattedProperty(row, column.mnemonic, columnType);
        const clickCallback = column.click_callback ? column.click_callback : null;

        if (column.special_render === "tape_photo") {
            return (
                row?.["media_type"] ?
                    (<img onClick={() => {
                        if (clickCallback) clickCallback(row);
                    }} style={{height: '75px', cursor: 'pointer'}} src={getTapeIcon(formattedProperty)} alt={"Rendered SVG icon of the tape"}/>)
                    : (<div></div>)
            );
        }

        // The API's status string is 'Unknown' for every status past Uploaded, because the view
        // that derives it stops at 4. The raw id is correct, so resolve the name from that.
        if (column.special_render === "tape_status") {
            return <span>{tapeStatusLabel(row)}</span>;
        }

        const content = ((dataContainsFormatting && row?.attributes?.[column.mnemonic]?.searchable) || (!dataContainsFormatting && column?.searchable) ?
                highlightText(row.item_id, formattedProperty)
                : formattedProperty
        );

        return (clickCallback ?
            <span onClick={() => {
                clickCallback(row)
            }} style={{cursor: "pointer"}}>{content}</span>
            : <span>{content}</span>);
    };


    /**
     * function to render the table cell. It generates the cell content based on the column and row data.
     * @param row
     * @param {number} rowIndex
     * @param column
     * @returns {JSX.Element}
     */
    const dynamicTableCell = (row, rowIndex, column) => {
        const uniqueKey = `DynamicTableCell-item_id-${row.item_id}-column-${column.mnemonic}-${column.name}`;

        // return empty cell if no column
        if (!column) return (
            <TableCell colSpan={2} align="center" key={uniqueKey}>
                <div style={{textAlign: "center", width: "50%", margin: "0", left: "50%", alignItems: "center", color: "#fff"}}></div>
            </TableCell>);

        const dynamicContentForCell = dynamicContentForCellHelper(column, row);
        const cellSx = {
            ...(column.shrink ? {width: '1%', whiteSpace: 'nowrap'} : {}),
        };
        const alignMap = {left: 'flex-start', center: 'center', right: 'flex-end'};
        const cellContent = (
            <Typography sx={{color: "#FFFFFF", fontSize: 14, fontFamily: "Poppins", fontWeight: row.selected ? 700 : 400}}
                        className={(dataContainsFormatting && row?.attributes?.[column.mnemonic]?.searchable) || (!dataContainsFormatting && column?.searchable) ? ("restore-name") : ""}
                        id={`restore-${column.mnemonic}-${rowIndex}`}
                        component="div"
            >
                {column.tooltip ? (() => {
                    const tooltipTitle = dynamicTooltipConstruction(row, column?.tooltip);
                    return tooltipTitle ? (
                        <Tooltip title={tooltipTitle} slotProps={{tooltip: {className: "wideTooltip"}}}>
                            {dynamicContentForCell}
                        </Tooltip>
                    ) : dynamicContentForCell;
                })() : dynamicContentForCell}
            </Typography>
        );
        return (
            <TableCell key={uniqueKey} sx={cellSx}>
                {column.content_align
                    ? <Box sx={{display: 'flex', justifyContent: alignMap[column.content_align] || 'flex-start', gap: 1}}>{cellContent}</Box>
                    : cellContent}
            </TableCell>);
    }

    const customRenderInTableCell = (row, rowIndex, column) => {
        const uniqueKey = `CustomRenderTableCell-item_id-${row.item_id}-column-${column.mnemonic}-${column.name}`;

        // return empty cell if no column
        if (!column) return (
            <TableCell colSpan={2} align="center" key={uniqueKey}>
                <div style={{textAlign: "center", width: "50%", margin: "0", left: "50%", alignItems: "center", color: "#fff"}}></div>
            </TableCell>);

        const dynamicContentForCell = column.custom_render(row);
        const cellSx = {
            ...(column.shrink ? {width: '1%', whiteSpace: 'nowrap'} : {}),
        };
        const alignMap = {left: 'flex-start', center: 'center', right: 'flex-end'};
        const justifyContent = column.content_align ? alignMap[column.content_align] : 'space-around';
        const tooltipTitle = column.tooltip ? dynamicTooltipConstruction(row, column?.tooltip) : null;
        const cellContent = tooltipTitle ? (
            <Tooltip title={tooltipTitle} slotProps={{tooltip: {className: "wideTooltip"}}}>
                {dynamicContentForCell}
            </Tooltip>
        ) : dynamicContentForCell;
        return (
            <TableCell key={uniqueKey} sx={cellSx}>
                <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent, gap: 1}}>
                    {cellContent}
                </Box>
            </TableCell>);
    };

    return (
        <>
            {/*Table in which the components of tape list or files list will be rendered*/}
            <Table>
                <TableHead>
                    <TableRow>
                        {columnsConfig ?
                            columnsConfig.map((column, idx) => (
                                <TableCell className={classes.columnHeader} key={`TableColumn-${idx}`} sx={column.shrink ? {width: '1%', whiteSpace: 'nowrap'} : {}}>
                                    <Typography sx={{
                                        ...{
                                            marginBottom: 0,
                                            color: "#FFFFFF",
                                            fontSize: 14,
                                            fontWeight: "700"
                                        },
                                        ...(column.header_align ? {textAlign: column.header_align} : {})
                                    }}>
                                        {column.name}
                                    </Typography>
                                </TableCell>
                            )) : (
                                <TableCell colSpan={2} align="center">
                                    <div style={{textAlign: "center", width: "50%", margin: "0", left: "50%", alignItems: "center", color: "#fff"}}>Data not Found</div>
                                </TableCell>
                            )}
                    </TableRow>
                </TableHead>

                <TableBody sx={{"& > *": {backgroundColor: "#3A3E3F", borderRadius: "8px",},}}>
                    {data.length > 0 ? data.map((row, rowIndex) => (
                            <TableRow key={`TableDynamicRow-${row.item_id}`} sx={{"& > *": {border: "unset"}}} data-testid={`table-row-${rowIndex}`}>
                                {columnsConfig ? (columnsConfig.map((column) => column.custom_render ? customRenderInTableCell(row, rowIndex, column) : dynamicTableCell(row, rowIndex, column))) : noDataCell()}
                            </TableRow>
                        )) :
                        <TableRow>
                            <TableCell colSpan={2} align="center">
                                <div style={{textAlign: "center", width: "50%", margin: "0", left: "50%", alignItems: "center", color: "#fff"}}>Data not Found</div>
                            </TableCell>
                        </TableRow>}
                </TableBody>
            </Table>
            <TablePagination
                showPagination={showPagination}
                entriesCount={entriesCount}
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChangeCallback={handlePageChangeCallback}
            />
        </>);
};
export default DataTable;